import {
  createRouter,
  createWebHistory,
  NavigationGuardNext,
  RouteLocationNormalized,
} from 'vue-router';

const Default = () => import('../layouts/Default.vue');
const FullPage = () => import('../layouts/FullPage.vue');

import { getPermissionForPath, isWipBlocked } from '../config/pagePermissions';
import { DiscountType } from '../graphql/generated/types';
import { useEditBarStore } from '../stores/editBarStore';
import { useLoginStore } from '../stores/login';
import { hasFullAccessHotfix } from '../stores/permissionStore';
const routes = [
  {
    path: '/auth/:token?',
    name: 'auth',
    component: () => import('../views/AuthPage.vue'),
    meta: { public: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginPage.vue'),
    meta: { public: true }, // ← 不需 token
  },
  {
    path: '/login/forgot-password',
    name: 'forgotPassword',
    component: () => import('../views/ForgotPasswordPage.vue'),
    meta: { public: true },
  },
  {
    path: '/login/verification-sent',
    name: 'verificationSent',
    component: () => import('../views/VerificationSentPage.vue'),
    meta: { public: true },
  },
  {
    path: '/verify-email',
    name: 'settingPassword',
    component: () => import('../views/SettingPasswordPage.vue'),
    meta: { public: true },
  },
  {
    path: '/recovery-password',
    name: 'resetPassword',
    component: () => import('../views/ResetPasswordPage.vue'),
    meta: { public: true },
  },
  {
    path: '/headquarter',
    name: 'chooseHeadquarter',
    component: () => import('../views/ChooseHeadquarter.vue'),
    beforeEnter: async (
      _to: RouteLocationNormalized,
      _from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      const loginStore = useLoginStore();
      await loginStore.switchHeadquarter();
      next();
    },
  },
  {
    path: '/versionAPI',
    component: Default,
    children: [
      {
        name: 'versionAPI',
        path: '',
        component: () => import('../components/VersionAPI.vue'),
        // meta: { public: true }, // ← 不需 token
      },
    ],
  },
  {
    path: '/operation-reports',
    name: 'operationReports',
    component: Default,
    children: [
      {
        name: 'itemAnalysis',
        path: 'item-analysis',
        component: () => import('../views/ItemAnalysis.vue'),
      },
      {
        name: 'closingSummary',
        path: 'closing-summary',
        component: () => import('../views/ClosingSummary.vue'),
      },
      {
        name: 'cashDrawerManagement',
        path: 'cash-drawer-management',
        component: () => import('../views/CashDrawerManagement.vue'),
      },
      {
        name: 'labelAnalysis',
        path: 'label-analysis',
        component: () => import('../views/LabelAnalysis.vue'),
      },
      {
        name: 'discountAnalysis',
        path: 'discount-analysis',
        component: () => import('../views/DiscountAnalysis.vue'),
      },
      {
        name: 'performance',
        path: 'performance',
        component: () => import('../views/PerformancePage.vue'),
      },
    ],
  },
  {
    path: '/sales-reports',
    name: 'salesReports',
    component: Default,
    children: [
      {
        name: 'order',
        path: 'order',
        component: () => import('../views/OrderPage.vue'),
      },
      {
        name: 'orderDetail',
        path: 'order-detail/:id',
        component: () => import('../views/OrderDetail.vue'),
      },
      {
        name: 'transaction',
        path: 'transaction',
        component: () => import('../views/TransactionPage.vue'),
      },
      {
        name: 'invoice',
        path: 'invoice',
        component: () => import('../views/InvoicePage.vue'),
      },
    ],
  },
  {
    path: '/online-order-reports',
    name: 'onlineOrderReports',
    component: Default,
    children: [
      {
        name: 'onlineOrderOverview',
        path: 'overview',
        component: () => import('../views/OnlineOrderOverview.vue'),
      },
      {
        name: 'onlineOrderRecord',
        path: 'record',
        component: () => import('../views/OnlineOrderRecord.vue'),
      },
    ],
  },
  {
    path: '/product-management',
    name: 'productManagement',
    component: Default,
    children: [
      {
        name: 'categories',
        path: 'categories',
        component: () => import('../views/CategoryPage.vue'),
      },
      {
        name: 'categoryAdd',
        path: 'category/add',
        component: () => import('../views/CategoryEdit.vue'),
      },
      {
        name: 'categoryEdit',
        path: 'category/edit/:id?',
        component: () => import('../views/CategoryEdit.vue'),
      },
      {
        name: 'items',
        path: 'items',
        component: () => import('../views/ItemPage.vue'),
      },
      {
        name: 'containerWeightManagement',
        path: 'container-weight',
        component: () => import('../views/ContainerWeightManagement.vue'),
      },
      {
        name: 'itemAdd',
        path: 'item/add',
        component: () => import('../views/ItemEdit.vue'),
      },
      {
        name: 'itemEdit',
        path: 'item/edit/:id?',
        component: () => import('../views/ItemEdit.vue'),
      },
      {
        name: 'labels',
        path: 'labels',
        component: () => import('../views/LabelPage.vue'),
      },
      {
        name: 'labelGroups',
        path: 'label-groups',
        component: () => import('../views/LabelGroup.vue'),
      },
      {
        name: 'labelGroupAdd',
        path: 'label-group/add',
        component: () => import('../views/LabelGroupEdit.vue'),
      },
      {
        name: 'labelGroupEdit',
        path: 'label-group/edit/:id?',
        component: () => import('../views/LabelGroupEdit.vue'),
      },
      {
        name: 'bundles',
        path: 'bundles',
        component: () => import('../views/BundlePage.vue'),
      },
      {
        name: 'bundleAdd',
        path: 'bundle/add',
        component: () => import('../views/BundleEdit.vue'),
      },
      {
        name: 'bundleEdit',
        path: 'bundle/edit/:id?',
        component: () => import('../views/BundleEdit.vue'),
      },
      {
        name: 'serviceWare',
        path: 'service-ware',
        component: () => import('../views/ServiceWare.vue'),
      },
    ],
  },
  {
    path: '/discount',
    component: Default,
    children: [
      {
        name: 'discount',
        path: '',
        component: () => import('../views/DiscountPage.vue'),
      },
      {
        path: 'add',
        name: 'discountAdd',
        component: () => import('../views/DiscountEdit.vue'),
        beforeEnter: (
          to: RouteLocationNormalized,
          _from: RouteLocationNormalized,
          next: NavigationGuardNext,
        ) => {
          const allowedTypes = [
            DiscountType.Order,
            DiscountType.SingleItem,
            DiscountType.SpecifiedItem,
            DiscountType.DeliveryFee,
          ];
          const id = to.query.type as DiscountType | undefined;
          if (id && allowedTypes.includes(id)) {
            next();
          } else {
            next('/');
          }
        },
      },
      {
        path: 'edit/:id',
        name: 'discountEdit',
        component: () => import('../views/DiscountEdit.vue'),
      },
    ],
  },
  {
    path: '/order-settings',
    name: 'orderSettings',
    children: [
      {
        path: 'menu',
        component: Default,
        children: [
          {
            name: 'menu',
            path: '',
            component: () => import('../views/MenuPage.vue'),
          },
        ],
      },
      {
        path: 'menu/:id',
        component: FullPage,
        children: [
          {
            name: 'menuEdit',
            path: '',
            component: () => import('../views/MenuEdit.vue'),
          },
        ],
      },
      {
        path: 'keypad',
        component: Default,
        children: [
          {
            name: 'keypad',
            path: '',
            component: () => import('../views/KeypadPage.vue'),
          },
        ],
      },
      {
        path: 'keypad/edit/:id?',
        component: FullPage,
        children: [
          {
            name: 'keypadEdit',
            path: '',
            component: () => import('../views/KeypadEdit.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/table-management',
    name: 'tableManagement',
    children: [
      {
        path: '',
        component: Default,
        children: [
          {
            name: 'table',
            path: '',
            component: () => import('../views/TablePage.vue'),
          },
        ],
      },
      {
        path: 'edit/:id?',
        component: FullPage,
        children: [
          {
            name: 'tableEdit',
            path: '',
            component: () => import('../views/TableEdit.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/shop',
    component: Default,
    children: [
      {
        name: 'shop',
        path: '',
        component: () => import('../views/ShopPage.vue'),
      },
      {
        name: 'shopEdit',
        path: 'edit/:id?',
        component: () => import('../views/ShopEdit.vue'),
      },
    ],
  },
  {
    path: '/device-management',
    name: 'deviceManagement',
    children: [
      {
        path: 'overview',
        component: Default,
        children: [
          {
            name: 'overview',
            path: '',
            component: () => import('../views/OverviewPage.vue'),
          },
        ],
      },
      {
        path: 'pos',
        component: Default,
        children: [
          {
            name: 'pos',
            path: '',
            component: () => import('../views/PosPage.vue'),
          },
        ],
      },
      {
        path: 'pos-setting',
        component: Default,
        children: [
          {
            name: 'posSetting',
            path: '',
            component: () => import('../views/PosSetting.vue'),
          },
        ],
      },
      {
        path: 'customer-display',
        component: Default,
        children: [
          {
            name: 'customerDisplay',
            path: '',
            component: () => import('../views/CustomerDisplayView.vue'),
          },
        ],
      },
      {
        path: 'printer',
        component: Default,
        children: [
          {
            name: 'printer',
            path: '',
            component: () => import('../views/PrinterPage.vue'),
          },
        ],
      },
      {
        path: 'printer/:type',
        component: FullPage,
        children: [
          {
            name: 'printerTypeList',
            path: '',
            component: () => import('../views/PrinterStyleEdit.vue'),
            children: [
              {
                name: 'printerEdit',
                path: ':printerID',
                component: () =>
                  import('../components/PrinterEdit/PrinterStyleList.vue'),
                props: (r: RouteLocationNormalized) => ({
                  printerType: r.params.type,
                  printerID: Number(r.params.printerID),
                }),
              },
              {
                name: 'printerStyleEdit',
                path: ':printerID/:ticketOf/:ticketID',
                component: () =>
                  import('../components/PrinterEdit/PrinterStyleEdit.vue'),
                props: (r: RouteLocationNormalized) => ({
                  printerType: r.params.type,
                  printerID: Number(r.params.printerID),
                  ticketOf: r.params.ticketOf,
                  ticketID: Number(r.params.ticketID),
                }),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '/staff-management',
    name: 'staffManagement',
    children: [
      {
        path: 'staff',
        component: Default,
        children: [
          {
            name: 'staffPage',
            path: '',
            component: () => import('../views/StaffPage.vue'),
          },
        ],
      },
      {
        path: 'staff/add',
        component: Default,
        children: [
          {
            name: 'addStaff',
            path: '',
            component: () => import('../views/StaffEdit.vue'),
          },
        ],
      },
      {
        path: 'staff/edit/:id',
        component: Default,
        children: [
          {
            name: 'staffEdit',
            path: '',
            component: () => import('../views/StaffEdit.vue'),
          },
        ],
      },
      {
        path: 'roles',
        component: Default,
        children: [
          {
            name: 'staffRoles',
            path: '',
            component: () => import('../views/RolePage.vue'),
          },
        ],
      },
      {
        path: 'roles/edit/:roleTemplateID',
        component: Default,
        children: [
          {
            name: 'roleEdit',
            path: '',
            component: () => import('../views/RoleEdit.vue'),
          },
        ],
      },
      {
        path: 'authorization',
        component: Default,
        children: [
          {
            name: 'authorization',
            path: '',
            component: () => import('../views/AuthorizationPage.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/ai-waiter',
    name: 'aiWaiter',
    children: [
      {
        path: 'basic-setting',
        component: Default,
        children: [
          {
            name: 'aiWaiterBasicSetting',
            path: '',
            component: () => import('../views/AiWaiterBasicSetting.vue'),
          },
        ],
      },
      {
        path: 'virtual-human',
        component: Default,
        children: [
          {
            name: 'aiWaiterVirtualHuman',
            path: '',
            component: () => import('../views/AiWaiterVirtualHuman.vue'),
          },
        ],
      },
      {
        path: '',
        redirect: { name: 'aiWaiterBasicSetting' },
      },
    ],
  },
  {
    path: '/o2o-management',
    name: 'o2oManagement',
    children: [
      {
        path: 'online-order',
        component: Default,
        children: [
          {
            name: 'onlineOrder',
            path: 'basic-setting',
            component: () => import('../views/OnlineOrder.vue'),
          },
          {
            name: 'mealTakeMethod',
            path: 'meal-take-method',
            component: () => import('../views/OnlineOrder.vue'),
          },
          {
            name: 'orderingTime',
            path: 'ordering-time',
            component: () => import('../views/OnlineOrder.vue'),
          },
          {
            path: '',
            redirect: () => {
              const codes: string[] = JSON.parse(
                sessionStorage.getItem('permissionCodes') || '[]',
              );
              const has = (prefix: string) =>
                hasFullAccessHotfix() ||
                codes.includes(`${prefix}.*.Read`) ||
                codes.includes(`${prefix}.*.Write`);
              if (has('O2O.PageO2OBasic')) return { name: 'onlineOrder' };
              if (has('O2O.PageO2ODiningOption'))
                return { name: 'mealTakeMethod' };
              if (has('O2O.PageO2OOrderingTime'))
                return { name: 'orderingTime' };
              return { name: 'versionAPI' };
            },
          },
        ],
      },
      {
        path: 'scan-order',
        component: Default,
        children: [
          {
            name: 'scanOrder',
            path: 'basic-setting',
            component: () => import('../views/ScanOrder.vue'),
          },
          {
            name: 'dineIn',
            path: 'dine-in',
            component: () => import('../views/ScanOrder.vue'),
          },
          {
            name: 'takeOut',
            path: 'take-out',
            component: () => import('../views/ScanOrder.vue'),
          },
          {
            path: '',
            redirect: () => {
              const codes: string[] = JSON.parse(
                sessionStorage.getItem('permissionCodes') || '[]',
              );
              const has = (prefix: string) =>
                hasFullAccessHotfix() ||
                codes.includes(`${prefix}.*.Read`) ||
                codes.includes(`${prefix}.*.Write`);
              if (has('O2O.PageScanOrderBasic')) return { name: 'scanOrder' };
              if (has('O2O.PageScanOrderDiningIn')) return { name: 'dineIn' };
              if (has('O2O.PageScanOrderDiningOut')) return { name: 'takeOut' };
              return { name: 'versionAPI' };
            },
          },
        ],
      },
      {
        path: 'multi-shop',
        component: Default,
        children: [
          {
            name: 'sameBrand',
            path: 'same-brand',
            component: () => import('../views/MultiShop.vue'),
          },
          {
            name: 'crossBrand',
            path: 'cross-brand',
            component: () => import('../views/MultiShop.vue'),
          },
          {
            path: '',
            redirect: () => {
              // 直接讀 sessionStorage，與 beforeEach 保持一致，
              // 避免 Pinia 在 router context 的初始化時序問題。
              const codes: string[] = JSON.parse(
                sessionStorage.getItem('permissionCodes') || '[]',
              );
              const canSameBrand =
                hasFullAccessHotfix() ||
                codes.includes('O2O.PageMultiShop.*.Read') ||
                codes.includes('O2O.PageMultiShop.*.Write');
              return { name: canSameBrand ? 'sameBrand' : 'crossBrand' };
            },
          },
        ],
      },
    ],
  },
  {
    path: '/application-management',
    component: Default,
    children: [
      {
        name: 'applicationManagement',
        path: '',
        component: () => import('../views/ApplicationPage.vue'),
      },
    ],
  },
  {
    path: '/system-publish',
    component: Default,
    children: [
      {
        name: 'systemPublish',
        path: '',
        component: () => import('../views/SystemPublish.vue'),
      },
    ],
  },
  {
    path: '/system-settings',
    component: Default,
    children: [
      {
        path: '',
        name: 'systemSettings',
        component: () => import('../components/SystemSettings.vue'),
        children: [
          {
            name: 'headquarter',
            path: 'headquarter',
            component: () => import('../views/HeadquarterPage.vue'),
          },
          {
            name: 'brand',
            path: 'brand',
            component: () => import('../views/BrandPage.vue'),
          },
          {
            name: 'taxes',
            path: 'taxes',
            component: () => import('../views/TaxesPage.vue'),
          },
          {
            name: 'diningOption',
            path: 'dining-option',
            component: () => import('../views/DiningOption.vue'),
          },
          {
            name: 'orderRemarks',
            path: 'order-remarks',
            component: () => import('../views/OrderRemark.vue'),
          },
          {
            name: 'posUpdate',
            path: 'pos-update',
            component: () => import('../views/PosUpdateSetting.vue'),
          },
          {
            name: 'paymentMethods',
            path: 'payment-methods',
            component: () => import('../views/PaymentMethod.vue'),
          },
          {
            name: 'serviceFee',
            path: 'service-fee',
            component: () => import('../views/ServiceFee.vue'),
          },
          {
            name: 'discountRule',
            path: 'discount-rule',
            component: () => import('../views/DiscountRule.vue'),
          },
          {
            name: 'minimumCharge',
            path: 'minimum-charge',
            component: () => import('../views/MinimumCharge.vue'),
          },
          {
            name: 'invoice-setting',
            path: 'invoice-setting',
            component: () => import('../views/InvoiceSetting.vue'),
          },
          {
            path: 'reason/:type',
            name: 'reason',
            component: () => import('../views/ReasonPage.vue'),
          },
        ],
      },
    ],
  },

  {
    path: '/customer-management',
    component: Default,
    children: [
      {
        name: 'members',
        path: 'members',
        component: () => import('../views/MembersPage.vue'),
      },
      {
        path: 'member/:memberSourceID/:cardCode',
        component: () => import('../views/MemberDetail.vue'),
        children: [
          {
            name: 'memberDetail',
            path: '',
            redirect: { name: 'memberInfo' },
          },
          {
            name: 'memberInfo',
            path: 'info',
            component: () =>
              import('../components/MemberDetail/MemberInfoTab.vue'),
          },
          {
            name: 'memberTransactions',
            path: 'transactions',
            component: () =>
              import('../components/MemberDetail/MemberTransactionTab.vue'),
          },
          {
            name: 'memberPoints',
            path: 'points',
            component: () =>
              import('../components/MemberDetail/MemberPointsTab.vue'),
          },
        ],
      },
      {
        name: 'frequentContacts',
        path: 'frequent-contacts',
        component: () => import('../views/FrequentContacts.vue'),
      },
      {
        name: 'memberLabels',
        path: 'member-labels',
        component: () => import('../views/MemberLabels.vue'),
      },
      {
        name: 'memberSystem',
        path: 'member-system',
        component: () => import('../views/MemberSystem.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    component: Default,
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// 全域守衛：wip 封鎖、auth 驗證、permission 檢查
router.beforeEach((to) => {
  const token = sessionStorage.getItem('authToken');
  const secret = sessionStorage.getItem('hmacSecret');
  const isPublic = to.matched.some((r) => r.meta.public === true);

  // wip 頁面（測試站以上）：不論登入狀態一律 redirect 回首頁，避免先導去 login
  // 再因為仍是 wip 而被二次攔截（且暴露「此路徑存在」的訊息）
  if (!isPublic && isWipBlocked(to.path)) {
    return { name: 'versionAPI' };
  }

  if (token && secret && to.name === 'login') {
    return { name: 'versionAPI' };
  }

  if ((!token || !secret) && !isPublic) {
    return { name: 'login' };
  }

  if (to.path === '/') {
    const hasAuth =
      sessionStorage.getItem('hmacSecret') &&
      sessionStorage.getItem('authToken');
    return hasAuth ? { name: 'versionAPI' } : { name: 'login' };
  }

  // 權限檢查：已登入且非公開頁面才檢查
  // 直接讀 sessionStorage，避免 Pinia store 在 router context 的初始化時序問題
  if (token && secret && !isPublic) {
    const permissionPrefix = getPermissionForPath(to.path);
    if (permissionPrefix !== undefined) {
      // 在白名單內：檢查使用者的 permissionCodes 是否包含 *.Read 或 *.Write
      const codes: string[] = JSON.parse(
        sessionStorage.getItem('permissionCodes') || '[]',
      );
      const allowed =
        hasFullAccessHotfix() ||
        codes.includes(`${permissionPrefix}.*.Read`) ||
        codes.includes(`${permissionPrefix}.*.Write`);
      if (!allowed) {
        return { name: 'versionAPI' };
      }
    }
    // 不在白名單內：預設放行
  }
});

router.afterEach(() => {
  const editBar = useEditBarStore();
  editBar.hide();
});

export default router;
